// Service Steady PWA — src/components/FilterSheet.tsx — 2026-06-15
//
// Bottom sheet (mobile) / centered modal (desktop) listing ALL conversation
// filters. Opened from the "Filters" button in FilterChips. Mirrors the
// LocationPickerModal sheet pattern (fixed overlay, rounded-t-2xl, X close,
// Esc / overlay-tap to dismiss). Presentation only — no filtering logic here.

import { useEffect } from 'react';
import { Check, X } from 'lucide-react';
import type { ConversationFilter } from '@/lib/api';
import { CHIP_DEFS, formatCount } from '@/components/FilterChips';

interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  value: ConversationFilter;
  onChange: (next: ConversationFilter) => void;
  counts?: Partial<Record<ConversationFilter, number>>;
}

export function FilterSheet({ open, onClose, value, onChange, counts }: FilterSheetProps) {
  // Esc closes (matches the overlay-tap dismiss).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const select = (next: ConversationFilter): void => {
    onChange(next);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Filter chats"
      onClick={onClose}
    >
      <div
        className="flex w-full max-h-[85vh] max-w-md flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl dark:bg-[#0F172A] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Grab handle (mobile) */}
        <div className="flex shrink-0 justify-center pt-2 sm:hidden" aria-hidden="true">
          <div className="h-1 w-9 rounded-full bg-ink-200 dark:bg-[#334155]" />
        </div>
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-ink-100 px-4 py-3 dark:border-[#1F2A37]">
          <div className="text-base font-semibold text-ink-900 dark:text-white">
            Filter chats
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full text-ink-500 hover:bg-ink-50 dark:text-[#94A3B8] dark:hover:bg-[#1E293B]"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filter list — flex-1 + scroll; bottom padding clears the fixed
            TabBar (--tab-h) + iOS home-indicator safe area so the last rows
            (Booked / Archived) never hide behind the nav. */}
        <ul
          role="listbox"
          aria-label="Filter chats"
          className="flex-1 divide-y divide-ink-100 overflow-y-auto dark:divide-[#1F2A37]"
          style={{ paddingBottom: 'calc(var(--tab-h, 64px) + env(safe-area-inset-bottom, 0px))' }}
        >
          {CHIP_DEFS.map((c) => {
            const active = c.value === value;
            const count = counts?.[c.value];
            const showCount = c.value === 'all' ? typeof count === 'number' : Boolean(count);
            const isNeeds = c.value === 'needs';
            return (
              <li key={c.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => select(c.value)}
                  className={[
                    'flex w-full items-center gap-3 px-4 py-2.5 text-left text-[15px] transition-colors',
                    active
                      ? 'bg-[#E6F1FB] font-semibold text-[#0C447C] dark:bg-[#15315A] dark:text-[#9CC4F0]'
                      : 'text-ink-800 hover:bg-ink-50 dark:text-[#E2E8F0] dark:hover:bg-[#1E293B]',
                  ].join(' ')}
                >
                  <Check
                    className={['h-4 w-4 flex-shrink-0', active ? 'opacity-100' : 'opacity-0'].join(' ')}
                    aria-hidden="true"
                  />
                  <span className="flex-1">{c.label}</span>
                  {showCount ? (
                    <span
                      className={[
                        'min-w-[20px] text-right text-[13px] font-semibold tabular-nums',
                        isNeeds
                          ? 'text-red-600 dark:text-red-400'
                          : active
                            ? 'text-[#0C447C] dark:text-[#9CC4F0]'
                            : 'text-ink-500 dark:text-[#94A3B8]',
                      ].join(' ')}
                    >
                      {formatCount(count ?? 0)}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
